import React from 'react';

import { IServerOrder } from '@rbi-ctg/menu';
import QRCode from 'components/qrcode';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import useOrderNumber from 'hooks/use-order-number';
import { PickupPin, QRContainer, QRWrapper } from 'pages/order-confirmation/common';
import OrderIssues from 'pages/order-confirmation/order-issues';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { usePickupTime } from 'state/order/hooks/use-pickup-time';
import { primitive } from 'styles/constants/primitives';
import { fullBrandName } from 'utils/environment';

import { MapCard } from '../components/map-card';
import { OrderConfirmationHeader } from '../components/order-confirmation-header';
import { OrderDetailsCard } from '../components/order-details-card';
import { ServiceModeHeading } from '../components/service-mode-heading';
import { Container, Section, SectionTitle } from '../styled';

const qrCodeOptions = {
  margin: 1,
  scale: 4,
  color: {
    dark: primitive.$black,
    light: primitive.$white,
  },
};

function PickupConfirmed({ serverOrder }: { serverOrder: IServerOrder }) {
  const isShowingOrderingIssue = useFlag(LaunchDarklyFlag.SHOW_ORDERING_ISSUE);

  const orderNumber = useOrderNumber(serverOrder);

  const { fulfillmentDetails } = serverOrder;
  const { pickupDate } = usePickupTime(serverOrder);

  const pickupOrderAlert = t('pickUpOrderSuccessfulAlert', {
    orderNumber,
    brand: fullBrandName(),
    address: `${serverOrder.cart.storeAddress.addressLine1} ${serverOrder.cart.storeAddress.addressLine2}`,
  });
  return (
    <>
      <Container>
        <Section>
          <OrderConfirmationHeader serverOrder={serverOrder} />
        </Section>

        <Section>
          <ServiceModeHeading serviceMode={serverOrder.cart.serviceMode} />
          <MapCard
            storeAddress={serverOrder.cart.storeAddress}
            storeDetails={serverOrder.cart.storeDetails}
            orderDate={pickupDate}
            serverOrderServiceMode={serverOrder.cart.serviceMode}
          />
        </Section>

        <Section>
          <SectionTitle focusable accessible accessibilityRole="header">
            {t('order')}
          </SectionTitle>
          <OrderDetailsCard order={serverOrder} />
        </Section>

        {!!fulfillmentDetails && (
          <Section>
            {!!fulfillmentDetails?.pickupQrCode && (
              <QRContainer>
                <QRWrapper>
                  <QRCode
                    barcode={fulfillmentDetails.pickupQrCode}
                    options={qrCodeOptions}
                    testID="qr-code"
                  />
                </QRWrapper>
              </QRContainer>
            )}

            {!!fulfillmentDetails?.pickupPin && (
              <PickupPin>{fulfillmentDetails.pickupPin}</PickupPin>
            )}
          </Section>
        )}

        {isShowingOrderingIssue && (
          <Section>
            <OrderIssues />
          </Section>
        )}
      </Container>

      <VisuallyHidden role="alert" accessibilityLabel={pickupOrderAlert} />
    </>
  );
}

export default React.memo(PickupConfirmed);
